<script>
import { range, cloneDeep } from 'lodash-es';
import { mapState, mapActions, mapGetters } from 'vuex';
import Exceptions from './ExceptionTypes';
import triggers from '../../variables/triggers';

export default {
  name: 'TriggerRulesTemplate',
  components: {
    ...Exceptions,
  },
  props: {
    template: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      localTemplate: cloneDeep(this.template),
      exceptionPanel: undefined,
      templatesMenuProps: {
        closeOnContentClick: true,
        closeOnClick: true,
        value: false,
        openOnClick: true,
      },
      snackbarShow: false,
    };
  },
  computed: {
    ...mapState({
      templates: state => state.triggers.rules.templates,
      domain: state => state.triggers.rules.domain,
      triggerType: state => state.triggers.rules.triggerType,
    }),
    ...mapGetters({
      exceptionTypes: 'triggers/getExceptionTypes',
      usedTemplates: 'triggers/usedTemplates',
    }),
    daysOptions() {
      return range(1, 31);
    },
    daysPlaceholder() {
      if (this.triggerType.dueDateOptions.length > 1) {
        return this.localTemplate.order === 1 ? 'Set days before the due date' : 'Set days after the last trigger';
      }
      return this.triggerType.dueDateOptions[0] === triggers.TRIGGER_TYPE_DUE_DATE_BEFORE
        ? 'Set days before the due date'
        : 'Set days after the last trigger';
    },
    daysLabel() {
      if (this.triggerType.dueDateOptions.length > 1) {
        return this.localTemplate.order === 1 ? 'Days before' : 'Days after last';
      }
      return this.triggerType.dueDateOptions[0] === triggers.TRIGGER_TYPE_DUE_DATE_BEFORE
        ? 'Days before'
        : 'Days after';
    },
    timeOfDayOptions() {
      return range(0, 24);
    },
    filteredExceptionTypes() {
      return this.exceptionTypes.filter(
        exceptionType => exceptionType.compatibleTriggers.includes(this.template.triggerType.id));
    },
    filteredTemplates() {
      return this.templates.items.filter(
        item => (
          (item.id === this.template.templateId || !this.usedTemplates[item.id])
          && item.triggerType.id === this.triggerType.id
        ),
      );
    },
  },
  methods: {
    ...mapActions({
      getTemplates: 'triggers/getTemplatesForRules',
      updateTemplate: 'triggers/updateRuleTemplate',
    }),
    refreshTemplates() {
      if (this.templates.loading) {
        return;
      }
      this.getTemplates().then(() => {
        if (this.templates.error) {
          this.snackbarShow = true;
        } else {
          this.templatesMenuProps.value = true;
        }
      });
    },
    addException() {
      this.localTemplate.exceptions.push({
        id: this.$moment().valueOf(),
        type: undefined,
        data: {},
      });
      this.exceptionPanel = this.localTemplate.exceptions.length - 1;
    },
    getException(type) {
      return this.filteredExceptionTypes.find(e => e.type === type);
    },
    deleteException(exceptionId) {
      const index = this.localTemplate.exceptions.findIndex(e => e.id === exceptionId);
      this.localTemplate.exceptions.splice(index, 1);
    },
    updateException({ id, data }) {
      const index = this.localTemplate.exceptions.findIndex(e => e.id === id);
      this.$set(this.localTemplate.exceptions[index], 'data', data);
    },
    validate() {
      if (!this.localTemplate.timeOfDay ||
        !this.localTemplate.daysAfterLast ||
        !this.localTemplate.templateId
      ) {
        return false;
      }
      return this.validateExceptions();
    },
    validateExceptions() {
      if (!this.localTemplate.exceptions.length) {
        return true;
      }
      return !this.localTemplate.exceptions.some(
        exception => exception.data && !exception.data.valid,
      );
    },
    daysDescription(template) {
      if (this.triggerType.dueDateOptions.length > 1) {
        return template.order === 1
          ? 'The days before the due date this trigger should be sent'
          : 'The days after last trigger this trigger should be sent';
      }
      return this.triggerType.dueDateOptions[0] === triggers.TRIGGER_TYPE_DUE_DATE_BEFORE
        ? 'The days before arrival date this trigger should be sent'
        : 'The days after departure date this trigger should be sent';
    },
  },
  watch: {
    localTemplate: {
      deep: true,
      handler() {
        const template = cloneDeep(this.localTemplate);
        template.valid = this.validate();
        this.updateTemplate(template);
      },
    },
  },
  created() {
    this.$set(this.localTemplate, 'valid', this.validate());
  },
};
</script>

<template>
  <v-layout>
    <v-flex xs12 md6 px-4>
      <v-layout wrap>
        <v-flex xs12 text-xs-center>
          <h3>Setup</h3>
        </v-flex>
        <v-flex xs12 mt-3>
          <v-select
            v-model="localTemplate.timeOfDay"
            :items="timeOfDayOptions"
            :disabled="disabled"
            placeholder="Set the time of day to send the triggers"
            label="Time of day"
          >
            <template v-slot:item="{ item }">
              <span v-if="item < 10">0{{ item }}:00</span>
              <span v-else>{{ item }}:00</span>
            </template>
            <template v-slot:selection="{ item }">
              <span v-if="item < 10">0{{ item }}:00</span>
              <span v-else>{{ item }}:00</span>
            </template>
            <template v-slot:prepend>
              <v-tooltip left max-width="300px">
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">info</v-icon>
                </template>
                <div class="pa-2">
                  The time of the day the trigger is sent out at.
                  <br>
                  <br>
                  Note that this is the time the trigger will be queued at,
                  which means the actual mail sent to the customer
                  can be delayed if the server is busy
                </div>
              </v-tooltip>
            </template>
          </v-select>
        </v-flex>
        <v-flex xs12>
          <v-select
            v-model="localTemplate.daysAfterLast"
            :placeholder="daysPlaceholder"
            :items="daysOptions"
            :label="daysLabel"
            :disabled="disabled"
            type="number"
          >
            <template v-slot:prepend>
              <v-tooltip max-width="300px" left>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">info</v-icon>
                </template>
                <div class="pa-2">
                  <template>
                    {{ daysDescription(template) }}
                  </template>
                  <br>
                  <br>
                  <template v-if="triggerType.dueDateOptions.length > 1">
                    Only whole days are used, rounded up.
                    So if a trigger is sent 23:00 on tuesday, with a reminder
                    to be sent 2 days later, that trigger will be sent thursday
                  </template>
                </div>
              </v-tooltip>
            </template>
          </v-select>
        </v-flex>
        <v-flex xs12>
          <v-select
            v-model="localTemplate.templateId"
            :loading="templates.loading"
            :items="filteredTemplates"
            :disabled="templates.loading || disabled"
            :menu-props="templatesMenuProps"
            item-text="name"
            item-value="id"
            placeholder="Select A Template"
            label="Template"
          >
            <template v-slot:append>
              <v-icon
                :class="{ 'icon-spin': templates.loading }"
                @click.stop="refreshTemplates"
              >
                refresh
              </v-icon>
            </template>
            <template v-slot:prepend>
              <v-tooltip left>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">info</v-icon>
                </template>
                <div>
                  The template to use for the trigger email
                  <br>
                  <br>
                  Note that the template content is not saved for each revision,
                  and can always be updated
                </div>
              </v-tooltip>
            </template>
            <template v-slot:no-data>
              <a
                v-if="templates.noContent"
                :href="`templates?domain=${domain.id}`"
                class="external-link"
                target="_blank"
              >
                <div class="pa-2 body-1 text-xs-center grey--text cursor-pointer">
                  No templates have been created, click to create one
                </div>
              </a>
              <div v-else-if="templates.items" class="pa-2 body-1 text-xs-center grey--text">
                No templates available
              </div>
              <div v-else class="pa-2 body-1 text-xs-center grey--text">
                Unable to load templates
              </div>
            </template>
          </v-select>
        </v-flex>
      </v-layout>
    </v-flex>

    <v-divider vertical/>

    <v-flex xs12 md6 px-4>
      <v-layout wrap>
        <v-flex xs12 text-xs-center>
          <h3>Exceptions</h3>
        </v-flex>
        <v-flex xs12 mt-3>
          <v-btn :disabled="disabled" color="info" block @click="addException">
            Add New Exception
          </v-btn>
          <v-expansion-panel v-model="exceptionPanel">
            <v-expansion-panel-content
              v-for="exception in localTemplate.exceptions"
              :key="`exception-${exception.id}`"
            >
              <template v-slot:header>
                <v-flex grow>
                  <b>Exception: </b>
                  <div v-if="exception.type" class="d-inline">
                    <b>{{ getException(exception.type).name }}</b>
                    <span
                      v-if="exception.data && !exception.data.valid"
                      class="caption error--text pl-3"
                    >
                      Missing setup
                    </span>
                    <template v-else>
                      <v-icon color="green" class="px-2" small>check</v-icon>
                      <span
                        v-for="label in getException(exception.type).labels"
                        :key="`exception-${exception.id}-label-${label.value}`"
                        class="caption grey--text pr-2"
                      >
                        {{ label.label }}: {{ exception.data[label.value] }}
                      </span>
                    </template>
                  </div>
                  <div v-else class="d-inline">
                    <span v-if="!exception.data.valid" class="caption error--text pl-3">
                      Missing type
                    </span>
                  </div>
                </v-flex>
                <v-flex shrink>
                  <v-btn
                    :disabled="disabled"
                    class="ma-0"
                    icon
                    small
                    @click.stop="deleteException(exception.id)"
                  >
                    <v-icon small>delete</v-icon>
                  </v-btn>
                </v-flex>
              </template>
              <v-divider/>
              <v-card-text>
                <v-layout wrap>
                  <v-flex xs12>
                    <v-select
                      v-model="exception.type"
                      :items="filteredExceptionTypes"
                      :disabled="disabled"
                      item-text="name"
                      item-value="type"
                      placeholder="Select exception type"
                      label="Exception type"
                      dense
                    />
                  </v-flex>
                  <v-flex xs12 mt-2>
                    <component
                      v-if="exception.type"
                      :is="exception.type"
                      :exception-id="exception.id"
                      :exception-data="exception.data"
                      :disabled="disabled"
                      @update="updateException"
                    />
                    <div v-else class="text-xs-center">
                      <span class="caption grey--text">
                        Select an exception type to choose options
                      </span>
                    </div>
                  </v-flex>
                </v-layout>
              </v-card-text>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-flex>
      </v-layout>
    </v-flex>

    <v-snackbar v-model="snackbarShow">Unable to load templates</v-snackbar>
  </v-layout>
</template>



// WEBPACK FOOTER //
// src/components/Triggers/TriggerRulesTemplate.vue